<template>
  <b-card class="border rounded-xl border-gray-200 mb-3">
    <!-- Header: Title & Switch Toggle -->
    <div class="flex items-center justify-between pb-1 mb-2 border-b">
      <div>
        <h5 class="text-base font-semibold text-[#333333] mb-0.5">
          Meta Conversion Tracking
        </h5>
        <p class="text-xs md:text-sm text-[#828282] mb-0">
          Aktifkan pelacakan konversi Meta untuk mengoptimalkan iklan Meta Anda.
        </p>
      </div>
      <b-form-checkbox
        id="toggle-meta-conversion-tracking"
        v-model="capiData.is_active"
        switch
        class="custom-cs-switch mb-0"
        @change="onToggleChange"
      />
    </div>

    <!-- Body: 2 Kolom (Grid 50:50) sesuai Figma saat Toggle ON -->
    <div
      v-show="capiData.is_active"
      class="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3"
    >
      <!-- KOLOM KIRI: Form Konfigurasi Meta Pixel & Meta Events -->
      <div class="space-y-3">
        <!-- 1. Nama Pixel -->
        <div>
          <label class="block text-sm font-medium text-[#333333] mb-1">
            Nama Pixel<span class="text-danger">*</span>
          </label>
          <b-form-input
            v-model="capiData.pixel_name"
            type="text"
            placeholder="Masukkan nama pixel Anda"
            class="text-sm custom-input-field"
            :disabled="isConfigSaved && !isEditingConfig"
          />
        </div>

        <!-- 2. Pixel ID -->
        <div>
          <label class="block text-sm font-medium text-[#333333] mb-1">
            Pixel ID<span class="text-danger">*</span>
          </label>
          <b-form-input
            v-model="capiData.pixel_id"
            type="text"
            placeholder="Masukkan 15–16 digit pixel ID Anda"
            class="text-sm custom-input-field"
            maxlength="16"
            :state="pixelIdState"
            :disabled="isConfigSaved && !isEditingConfig"
            @keypress="onlyNumber"
            @input="onPixelIdInput"
          />
          <div
            v-if="pixelIdError"
            class="text-xs text-danger mt-[3px]"
          >
            {{ pixelIdError }}
          </div>
        </div>

        <!-- 3. Token Akses CAPI -->
        <div>
          <label class="block text-sm font-medium text-[#333333] mb-1">
            Token Akses CAPI<span class="text-danger">*</span>
          </label>
          <div class="relative">
            <b-form-input
              v-model="capiData.access_token"
              :type="showToken ? 'text' : 'password'"
              placeholder="Masukkan token akses CAPI Anda"
              class="text-sm pr-10 custom-input-field"
              autocomplete="off"
              spellcheck="false"
              :disabled="isConfigSaved && !isEditingConfig"
            />
            <button
              type="button"
              class="absolute right-3 top-1/2 -translate-y-1/2 p-0 border-0 bg-transparent text-gray-400 hover:text-gray-600 cursor-pointer flex items-center justify-center focus:outline-none"
              tabindex="-1"
              :disabled="isConfigSaved && !isEditingConfig"
              @click="showToken = !showToken"
            >
              <feather-icon
                :icon="showToken ? 'EyeOffIcon' : 'EyeIcon'"
                size="16"
              />
            </button>
          </div>
        </div>

        <!-- 4. Kode Test Event -->
        <div>
          <label class="block text-sm font-medium text-[#333333] mb-1">
            Kode Test Event
          </label>
          <b-form-input
            v-model="capiData.event_test_code"
            type="text"
            placeholder="Masukkan kode test event Anda"
            class="text-sm custom-input-field"
            :disabled="isConfigSaved && !isEditingConfig"
          />
        </div>

        <!-- 5. Meta Events Section -->
        <div class="pt-1">
          <div class="flex items-center justify-between mb-1.5">
            <label class="block text-sm font-medium text-[#333333] mb-0">
              Meta Events<span class="text-danger">*</span>
            </label>
            <button
              type="button"
              :disabled="(isConfigSaved && !isEditingConfig) || isAllEventsSelected"
              class="font-semibold text-sm p-0 border-0 bg-transparent flex items-center gap-1 focus:outline-none"
              :class="[
                (isConfigSaved && !isEditingConfig) || isAllEventsSelected
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-[#F97316] hover:underline cursor-pointer'
              ]"
              @click="openSelectModal"
            >
              + Tambah Event
            </button>
          </div>

          <!-- Empty State Meta Events -->
          <div
            v-if="!activeMetaEvents.length"
            class="border-2 !border-dashed border-gray-300 rounded-lg p-[0.5rem] text-center cursor-pointer hover:border-[#F97316] hover:bg-orange-50/20 transition-all"
            @click="openSelectModal"
          >
            <div class="text-xs md:text-sm text-gray-400 font-medium">
              Belum ada event - Klik untuk menambahkan
            </div>
          </div>

          <!-- Chip List Meta Events -->
          <div
            v-else
            class="border border-gray-200 rounded-lg p-1 flex flex-wrap gap-1.5 bg-white min-h-[38px] items-center"
          >
            <div
              v-for="ev in activeMetaEvents"
              :key="ev"
              class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#FFF7ED] border border-[#FFEDD5] text-[#F97316] text-sm font-medium"
            >
              <span>{{ ev }}</span>
              <button
                v-if="!isConfigSaved || isEditingConfig"
                type="button"
                class="p-0 border-0 bg-transparent text-[#C2410C] hover:text-[#9A3412] cursor-pointer flex items-center justify-center focus:outline-none"
                title="Hapus event"
                @click.stop="removeEvent(ev)"
              >
                <feather-icon
                  icon="XIcon"
                  size="13"
                />
              </button>
            </div>
          </div>
        </div>

        <!-- Tombol Edit Pengaturan / Simpan Pengaturan -->
        <div class="pt-1">
          <!-- State 1: Terkoneksi & Readonly -> Tombol Outline Edit Pengaturan -->
          <b-button
            v-if="isConfigSaved && !isEditingConfig"
            size="sm"
            variant="outline-secondary"
            class="w-full text-xs font-medium rounded-lg border-gray-300 text-[#333333] hover:bg-gray-50 flex items-center justify-center gap-1 py-1"
            @click="startEditConfig"
          >
            <div class="flex justify-center items-center gap-1">
              <feather-icon
                icon="EditIcon"
                size="14"
              />
              <span>Edit Pengaturan</span>
            </div>
          </b-button>

          <!-- State 2: Mode Edit -> Tombol Simpan Perubahan & Batal -->
          <div
            v-else-if="isConfigSaved && isEditingConfig"
            class="flex items-center gap-1.5"
          >
            <b-button
              size="sm"
              variant="outline-secondary"
              class="flex-1 text-xs py-1"
              :disabled="isLoadingSave"
              @click="cancelEditConfig"
            >
              Batal
            </b-button>
            <b-button
              size="sm"
              variant="primary"
              class="flex-1 text-xs py-1 flex items-center justify-center gap-1 shadow-none"
              :disabled="isLoadingSave"
              @click="saveMetaConfig"
            >
              <b-spinner
                v-if="isLoadingSave"
                small
                class="mr-1"
              />
              <span>Simpan Perubahan</span>
            </b-button>
          </div>

          <!-- State 3: Belum Disimpan -> Tombol Simpan Pengaturan -->
          <div
            v-else
          >
            <b-button
              variant="primary"
              size="sm"
              class="w-full text-sm font-semibold rounded-lg py-1 flex items-center justify-center gap-1 shadow-none"
              :disabled="isLoadingSave"
              @click="saveMetaConfig"
            >
              <b-spinner
                v-if="isLoadingSave"
                small
                class="mr-1"
              />
              <span>Simpan Pengaturan</span>
            </b-button>
          </div>
        </div>
      </div>

      <!-- KOLOM KANAN: Meta CAPI Test Log sesuai Figma -->
      <div class="flex flex-col h-full">
        <!-- Header Log & Action Buttons -->
        <div class="flex items-start justify-between mb-1.5 pb-1 border-b border-gray-100">
          <div>
            <h6 class="text-sm font-semibold text-[#333333] mb-0.5">
              Meta CAPI Test Log
            </h6>
            <p class="text-xs text-[#828282] mb-0">
              Riwayat event Conversion API tipe Test/Sandbox untuk brand aktif
            </p>
          </div>
          <div class="flex items-center gap-2">
            <!-- Test Tracking Button -->
            <!-- Wrapper span: disabled button tidak memicu event hover, tooltip perlu target non-disabled -->
            <span
              id="btn-test-tracking-wrapper"
              class="inline-flex"
            >
              <b-button
                id="btn-test-tracking"
                variant="outline-secondary"
                size="sm"
                class="text-xs rounded-lg border-gray-200 text-[#333333] hover:bg-gray-50 flex items-center gap-1 px-1"
                :disabled="isTestDisabled || isLoadingTest"
                @click="openTestModal"
              >
                <div class="flex items-center gap-1">
                  <div
                    v-if="isLoadingTest"
                    class="flex items-center justify-center"
                  >
                    <b-spinner small />
                  </div>
                  <div v-else>
                    <b-img
                      src="https://storage.googleapis.com/komerce/assets/icons/git-compare.svg"
                      width="13"
                      height="13"
                    />
                  </div>
                  <span>Test Tracking</span>
                </div>
              </b-button>
            </span>
            <b-tooltip
              target="btn-test-tracking-wrapper"
              :disabled="!isTestDisabled || isLoadingTest"
              triggers="hover"
              placement="bottom"
            >
              {{ testDisabledHint }}
            </b-tooltip>

            <!-- Refresh Log Button -->
            <feather-icon
              role="button"
              icon="RefreshCwIcon"
              class="text-black"
              size="14"
              :disabled="isLoadingLogs"
              :class="{ 'animate-spin': isLoadingLogs }"
              @click="fetchLogs"
            />
          </div>
        </div>

        <!-- Sub-header info bar di atas tabel (Figma: "48 entries" & "Hapus Semua") -->
        <div
          v-if="logs.length > 0"
          class="flex items-center justify-between py-0.5 mb-1 text-xs"
        >
          <span class="text-[#828282] font-medium">
            {{ logs.length }} entries
          </span>
          <button
            type="button"
            class="text-danger hover:underline font-medium text-xs p-0 border-0 bg-transparent cursor-pointer focus:outline-none"
            :disabled="isLoadingClearAll"
            @click="confirmClearAllLogs"
          >
            Hapus Semua
          </button>
        </div>

        <!-- Box Log Content -->
        <div
          class="rounded-xl flex-1 flex flex-col bg-white min-h-[260px] overflow-hidden"
          :class="[
            logs.length === 0
              ? 'border border-dashed border-gray-200 justify-center items-center p-3'
              : 'border border-gray-200 p-1'
          ]"
        >
          <!-- Empty State sesuai Figma -->
          <div
            v-if="logs.length === 0"
            class="text-center py-6"
          >
            <div class="text-sm font-bold text-[#333333] mb-0.5">
              Belum ada CAPI events test
            </div>
            <div class="text-xs text-[#828282] max-w-[280px] mx-auto">
              Kirim event dengan tombol Test Tracking untuk melihat log disini
            </div>
          </div>

          <!-- Tabel List Logs sesuai Figma (Event, Pixel ID, Status, Response, Aksi) -->
          <div
            v-else
            class="w-full overflow-x-auto max-h-[380px] overflow-y-auto"
          >
            <table class="w-full text-left text-xs border-collapse">
              <thead>
                <tr class="border-b border-gray-100 text-[#828282] font-semibold">
                  <th class="pb-1.5 pl-1">
                    Event
                  </th>
                  <th class="pb-1.5">
                    Pixel ID
                  </th>
                  <th class="pb-1.5 text-center">
                    Status
                  </th>
                  <th class="pb-1.5 text-center">
                    Response
                  </th>
                  <th class="pb-1.5 pr-1 text-right">
                    <!-- Aksi -->
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr
                  v-for="log in logs"
                  :key="log.id"
                  class="hover:bg-gray-50/60 transition-colors"
                >
                  <!-- 1. Event + Timestamp -->
                  <td class="py-1.5 pl-1">
                    <div class="font-semibold text-[#333333]">
                      {{ log.event }}
                    </div>
                    <div class="text-[11px] text-[#828282]">
                      {{ formatLogTime(log.event_date) }}
                    </div>
                  </td>

                  <!-- 2. Pixel ID -->
                  <td class="py-1.5 text-[#333333] font-mono text-[11px]">
                    {{ log.pixel_id || capiData.pixel_id || '-' }}
                  </td>

                  <!-- 3. Status Badge -->
                  <td class="py-1.5 text-center">
                    <span
                      class="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold"
                      :class="isLogSuccess(log) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
                    >
                      {{ isLogSuccess(log) ? 'Berhasil' : 'Gagal' }}
                    </span>
                  </td>

                  <!-- 4. HTTP Code Response -->
                  <td class="py-1.5 text-center font-mono text-[#333333]">
                    {{ log.response_status_code }}
                  </td>

                  <!-- 5. Tombol Aksi (Mata & Tong Sampah) -->
                  <td class="py-1.5 pr-1 text-right">
                    <div class="inline-flex items-center gap-1 justify-end">
                      <button
                        type="button"
                        class="p-1 text-gray-400 hover:text-gray-700 bg-white cursor-pointer focus:outline-none"
                        title="Lihat Detail Payload"
                        @click="openPayloadDetail(log)"
                      >
                        <feather-icon
                          icon="EyeIcon"
                          size="13"
                        />
                      </button>
                      <button
                        type="button"
                        class="p-1 text-danger bg-white cursor-pointer focus:outline-none"
                        title="Hapus Log Ini"
                        @click="confirmDeleteSingleLog(log)"
                      >
                        <feather-icon
                          icon="Trash2Icon"
                          size="13"
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Pilih Meta Event -->
    <ModalSelectMetaEvent
      v-model="isModalOpen"
      :active-events="activeMetaEvents"
      @save="onSaveSelectedEvents"
    />

    <!-- Modal Detail Payload -->
    <ModalMetaCapiPayload
      v-model="isDetailModalOpen"
      :payload-data="selectedLogPayload"
    />

    <!-- Modal Test CAPI Event sesuai Figma -->
    <ModalTestMetaCapiEvent
      v-model="isTestModalOpen"
      :active-events="activeMetaEvents"
      :default-test-code="capiData.event_test_code || ''"
      :is-loading="isLoadingTest"
      @submit="onExecuteTestEvent"
    />
  </b-card>
</template>

<script>
import ModalSelectMetaEvent from './ModalSelectMetaEvent.vue'
import ModalMetaCapiPayload from './ModalMetaCapiPayload.vue'
import ModalTestMetaCapiEvent from './ModalTestMetaCapiEvent.vue'
import { META_EVENT_DEFINITIONS } from '../config'
import {
  apiTriggerMetaTestEvent,
  apiGetMetaCapiLogs,
  apiDeleteMetaCapiLog,
  apiClearMetaCapiLogs,
} from '../service/api.metaCapi.service'

export default {
  name: 'MetaConversionTracking',
  components: {
    ModalSelectMetaEvent,
    ModalMetaCapiPayload,
    ModalTestMetaCapiEvent,
  },
  props: {
    formData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showToken: false,
      isModalOpen: false,
      isDetailModalOpen: false,
      isTestModalOpen: false,
      isEditingConfig: false,
      configBackup: null,
      isLoadingSave: false,
      selectedLogPayload: {},
      pixelIdError: '',
      isLoadingTest: false,
      isLoadingLogs: false,
      isLoadingClearAll: false,
      logs: [],
    }
  },
  computed: {
    capiData() {
      let cfg = this.formData.meta_capi_config || this.formData.meta_capi
      if (!cfg) {
        cfg = {
          is_active: false,
          pixel_name: '',
          pixel_id: '',
          access_token: '',
          event_test_code: '',
          is_connected: false,
          dataset_name: '',
          meta_events: [],
          events: [],
        }
        this.$set(this.formData, 'meta_capi_config', cfg)
      }
      if (!Array.isArray(cfg.meta_events)) {
        const fallback = Array.isArray(cfg.events) ? [...cfg.events] : []
        this.$set(cfg, 'meta_events', fallback)
      }
      if (!Array.isArray(cfg.events)) {
        this.$set(cfg, 'events', [...cfg.meta_events])
      }
      return cfg
    },
    activeMetaEvents() {
      const { meta_events, events } = this.capiData || {}
      if (Array.isArray(meta_events) && meta_events.length) return meta_events
      if (Array.isArray(events) && events.length) return events
      return Array.isArray(meta_events) ? meta_events : []
    },
    isConfigSaved() {
      const d = this.capiData
      return !!(d.is_connected || (d.pixel_id && String(d.pixel_id).length >= 15))
    },
    isAllEventsSelected() {
      return this.activeMetaEvents.length >= META_EVENT_DEFINITIONS.length
    },
    pixelIdState() {
      if (this.pixelIdError) return false
      return null
    },
    isTestDisabled() {
      return !!this.testDisabledHint
    },
    testDisabledHint() {
      const d = this.capiData
      const pixelStr = String(d.pixel_id || '').trim()
      if (!(pixelStr && (pixelStr.length === 15 || pixelStr.length === 16))) {
        return 'Pixel ID belum valid (15-16 digit)'
      }
      if (this.pixelIdError) return this.pixelIdError
      if (!(d.access_token && d.access_token.trim())) return 'Token Akses CAPI belum diisi'
      if (!this.activeMetaEvents.length) return 'Pilih minimal 1 Meta Event'
      if (!(d.event_test_code && d.event_test_code.trim())) return 'Kode Test Event belum diisi'
      return ''
    },
  },
  watch: {
    'capiData.is_active': {
      handler(val) {
        if (val) {
          this.fetchLogs()
        }
      },
    },
  },
  mounted() {
    if (this.capiData.is_active) {
      this.fetchLogs()
    }
  },
  methods: {
    onToggleChange(val) {
      this.$emit('toggle-change', val)
      if (val && this.logs.length === 0) {
        this.fetchLogs()
      }
    },
    onlyNumber(event) {
      const charCode = event.which ? event.which : event.keyCode
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        event.preventDefault()
      }
    },
    onPixelIdInput(value) {
      const numeric = String(value || '').replace(/\D/g, '').slice(0, 16)
      this.$set(this.capiData, 'pixel_id', numeric)

      if (!numeric) {
        this.pixelIdError = ''
      } else if (numeric.length < 15 || numeric.length > 16) {
        this.pixelIdError = 'Pixel ID harus terdiri dari 15 atau 16 digit angka.'
      } else {
        this.pixelIdError = ''
      }
    },
    openSelectModal() {
      if (this.isConfigSaved && !this.isEditingConfig) return
      if (this.isAllEventsSelected) return
      this.isModalOpen = true
    },
    onSaveSelectedEvents(events) {
      const list = Array.isArray(events) ? [...events] : []
      this.$set(this.capiData, 'meta_events', list)
      this.$set(this.capiData, 'events', list)
    },
    removeEvent(eventVal) {
      if (this.isConfigSaved && !this.isEditingConfig) return
      const filtered = this.activeMetaEvents.filter(e => e !== eventVal)
      this.$set(this.capiData, 'meta_events', filtered)
      this.$set(this.capiData, 'events', filtered)
    },
    openTestModal() {
      if (this.isTestDisabled) return
      this.isTestModalOpen = true
    },
    startEditConfig() {
      this.configBackup = JSON.parse(JSON.stringify(this.capiData))
      this.isEditingConfig = true
    },
    cancelEditConfig() {
      if (this.configBackup) {
        Object.assign(this.capiData, JSON.parse(JSON.stringify(this.configBackup)))
      }
      this.pixelIdError = ''
      this.isEditingConfig = false
    },
    validateMetaConfig() {
      const { pixel_name, pixel_id, access_token } = this.capiData
      const pixelStr = String(pixel_id || '').trim()

      this.pixelIdError = !pixelStr
        ? 'Pixel ID wajib diisi.'
        : (pixelStr.length < 15 || pixelStr.length > 16)
          ? 'Pixel ID harus terdiri dari 15 atau 16 digit angka.'
          : ''

      const rules = [
        { isInvalid: !pixel_name?.trim(), message: 'Nama Pixel wajib diisi.' },
        { isInvalid: !!this.pixelIdError, message: this.pixelIdError },
        { isInvalid: !access_token?.trim(), message: 'Token Akses CAPI wajib diisi.' },
        { isInvalid: !this.activeMetaEvents.length, message: 'Minimal pilih 1 Meta Event.' },
      ]

      const error = rules.find(r => r.isInvalid)
      if (error) {
        this.$toast_error({ message: error.message })
        return false
      }

      return true
    },
    async saveMetaConfig() {
      if (!this.validateMetaConfig()) return

      try {
        this.isLoadingSave = true
        this.$set(this.capiData, 'is_connected', true)
        this.$set(this.capiData, 'dataset_name', this.capiData.pixel_name || '')
        this.$set(this.capiData, 'meta_events', [...this.activeMetaEvents])
        this.$set(this.capiData, 'events', [...this.activeMetaEvents])
        this.isEditingConfig = false
        this.configBackup = null
        this.$emit('save', this.capiData)
        this.$toast_success({ message: 'Pengaturan Meta Pixel berhasil disimpan' })
      } catch {
        this.$toast_error({ message: 'Validasi kredensial Meta gagal' })
      } finally {
        this.isLoadingSave = false
      }
    },
    async onExecuteTestEvent(testData) {
      const eventName = testData.event_name
      if (!eventName) return

      const rawEventKey = testData.event_key || eventName
      const isPurchase = rawEventKey === 'Purchase' || eventName.startsWith('Purchase')
      const isInitiateCheckout = rawEventKey === 'InitiateCheckout' || eventName.startsWith('Initiate Checkout') || eventName.startsWith('InitiateCheckout')
      const isLead = rawEventKey === 'Lead' || eventName.startsWith('Lead')

      const agentId = this.$route?.params?.id ? Number(this.$route.params.id) : null
      const customer = testData.customer_data || {}
      const technical = testData.technical_params || {}
      const testCode = testData.event_test_code || this.capiData.event_test_code || ''

      // Field user_data per event mengikuti payload di flow Figma
      const hasLocation = isLead || isInitiateCheckout || isPurchase
      const hasName = isInitiateCheckout || isPurchase

      let payload = {}

      try {
        this.isLoadingTest = true

        const customerData = {
          external_id: customer.external_id || '',
          phone_number: customer.phone_number || customer.phone || '',
        }
        if (hasName) {
          customerData.first_name = customer.first_name || ''
          customerData.last_name = customer.last_name || ''
        }
        if (hasLocation) {
          customerData.city = customer.city || ''
          customerData.province = customer.province || customer.state || ''
          customerData.zip_code = customer.zip_code || customer.zip || ''
          customerData.country_code = customer.country_code || customer.country || 'ID'
        }

        // Sesuaikan 100% dengan schema API contract
        payload = {
          pixel_id: this.capiData.pixel_id || '',
          event_test_code: testCode,
          access_token: this.capiData.access_token || '',
          cs_agent_id: agentId,
          event: testData.event_type === 'standard' ? 'Standar Meta Event' : testData.event_type || 'Standar Meta Event',
          event_name: eventName,
          fbp: technical.fbp || '',
          fbc: technical.fbc || '',
          customer_data: customerData,
        }

        // custom_data (price, currency, product_name, content_type, quantity)
        if (testData.custom_data) {
          const cd = testData.custom_data
          payload.custom_data = {
            price: Number(cd.price != null ? cd.price : cd.value) || 0,
            currency: cd.currency || 'IDR',
            product_name: cd.product_name || cd.content_name || '',
            content_type: cd.content_type || 'product',
            quantity: Number(cd.quantity != null ? cd.quantity : cd.num_items) || 1,
          }
        }

        await apiTriggerMetaTestEvent(payload)

        this.isTestModalOpen = false

        // Fetch logs otomatis dari server setelah trigger test
        await this.fetchLogs()

        this.$toast_success({ message: `Test event ${eventName} berhasil dikirim` })
      } catch (error) {
        const errorMessage = error?.response?.data?.meta?.message || 'Gagal mengirim test event ke Meta'
        this.logs.unshift({
          id: `${Date.now()}-${eventName}`,
          event: eventName,
          event_name: eventName,
          event_date: new Date().toISOString(),
          pixel_id: this.capiData.pixel_id,
          status: 'failed',
          response_status_code: error?.response?.status || 400,
          message: errorMessage,
          request_payload: payload,
          response_payload: error?.response?.data || { error: 'Unknown error' },
        })
        this.$toast_error({ message: errorMessage })
      } finally {
        this.isLoadingTest = false
      }
    },
    isLogSuccess(log) {
      return log.status === 'success'
    },
    formatLogTime(ts) {
      if (!ts) return '-'
      try {
        const d = new Date(ts)
        return Number.isNaN(d.getTime())
          ? ts
          : d.toLocaleTimeString('id-ID', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })
      } catch {
        return ts
      }
    },
    async fetchLogs() {
      const agentId = this.$route?.params?.id
      if (!agentId) return
      try {
        this.isLoadingLogs = true
        const { data } = await apiGetMetaCapiLogs(agentId)
        // Response swagger: data.data adalah array logs, data.page_info adalah paginasi
        const logList = data?.data?.data || data?.data || []
        this.logs = Array.isArray(logList) ? logList : []
      } catch (error) {
        // silent fallback
      } finally {
        this.isLoadingLogs = false
      }
    },
    openPayloadDetail(log) {
      this.selectedLogPayload = log
      this.isDetailModalOpen = true
    },
    async confirmDeleteSingleLog(log) {
      const { isConfirmed } = await this.$swal({
        title: 'Hapus Log Ini?',
        text: `Log event ${log.event_name || log.event} akan dihapus permanen.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ya, Hapus',
        cancelButtonText: 'Batal',
        customClass: {
          confirmButton: 'btn btn-danger mr-1',
          cancelButton: 'btn btn-outline-secondary',
        },
        buttonsStyling: false,
      })

      if (!isConfirmed) return

      try {
        await apiDeleteMetaCapiLog(log.id)
        this.logs = this.logs.filter(l => l.id !== log.id)
        this.$toast_success({ message: 'Log berhasil dihapus' })
      } catch (error) {
        this.$toast_error({ message: error?.response?.data?.meta?.message || 'Gagal menghapus log' })
      }
    },
    async confirmClearAllLogs() {
      const { isConfirmed } = await this.$swal({
        title: 'Hapus Semua Log?',
        text: 'Semua riwayat pengujian CAPI akan dihapus secara permanen.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Hapus Semua',
        cancelButtonText: 'Batal',
        customClass: {
          confirmButton: 'btn btn-danger mr-1',
          cancelButton: 'btn btn-outline-secondary',
        },
        buttonsStyling: false,
      })

      if (!isConfirmed) return

      try {
        this.isLoadingClearAll = true
        const agentId = this.$route?.params?.id || null
        if (agentId) {
          await apiClearMetaCapiLogs(agentId)
        }
        this.logs = []
        this.$toast_success({ message: 'Semua log CAPI berhasil dibersihkan' })
      } catch (error) {
        this.$toast_error({ message: error?.response?.data?.meta?.message || 'Gagal membersihkan log CAPI' })
      } finally {
        this.isLoadingClearAll = false
      }
    },
  },
}
</script>

<style scoped>
.custom-input-field {
  border-radius: 8px;
  border-color: #E2E8F0;
}
.custom-input-field:focus {
  border-color: #F97316;
  box-shadow: 0 0 0 1px #F97316;
}
/* Sembunyikan icon mata bawaan browser (seperti Microsoft Edge pada Windows) agar tidak dobel */
.custom-input-field::-ms-reveal,
.custom-input-field::-ms-clear,
input[type="password"]::-ms-reveal,
input[type="password"]::-ms-clear {
  display: none !important;
}
</style>
